import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Model } from 'app/model/backendmodel';
import { Metamodel } from 'app/model/base/model';

import { CopySourceAdapter, commonCopySourceAdapts } from './copysource.adapter';
import { OracleCopySource } from 'app/model/pipeline/copysource/oracle.copysource';
import { CopyActivity } from 'app/model/pipeline/copy.activity';
import { ValueAdapter } from 'app/model/adapters/value.adapter';
import { partitionOptionConst } from 'app/registries/consts/metadata-consts';
import { stringIsNullOrEmpty } from 'app/shared/utils/string.util';
import moment from 'moment-timezone';
import { isLogicAppExpression } from 'app/shared/utils/expression.util';

export const oracleCopySourceAdapts = {
    ...commonCopySourceAdapts,
    oracleReaderQuery: '*',
    partitionOption: '*',
    partitionSettings: {
        partitionColumnName: '*',
        partitionUpperBound: '*',
        partitionLowerBound: '*',
        partitionNames: '*'
    },
    queryTimeout: '*',
    convertDecimalToInteger: '*',
    numberPrecision : '*',
    numberScale: '*'
};

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(OracleCopySource),
    asynchronous: true,
    adapts: oracleCopySourceAdapts
})
export class OracleCopySourceAdapter extends CopySourceAdapter<OracleCopySource, Model.OracleSource> {
    protected override async _toResource(copySource: OracleCopySource): Promise<Model.OracleSource> {
        const result: Model.OracleSource = this.getCommonCopySourceProperties(
            copySource,
            Metamodel.modelOf(copySource).name
        );
        if (copySource.mode === 'query') {
            result.oracleReaderQuery = ValueAdapter.toResource(copySource, OracleCopySource.READER_QUERY); // need handle query keyword if we want to reuse the toTabularResource method
        }

        result.partitionOption = ValueAdapter.toResource(copySource, OracleCopySource.PARTITION_OPTION);
        if (OracleCopySource.PARTITION_OPTION.templateValue(copySource) != null) {
            result.partitionSettings = {
                partitionColumnName: ValueAdapter.toResource(copySource, OracleCopySource.PARTITION_COLUMN_NAME),
                partitionUpperBound: ValueAdapter.toResource(copySource, OracleCopySource.PARTITION_UPPER_BOUND),
                partitionLowerBound: ValueAdapter.toResource(copySource, OracleCopySource.PARTITION_LOWER_BOUND),
                partitionNames: ValueAdapter.toResource(copySource, OracleCopySource.PARTITION_NAMES)
            };
        } else if (
            !stringIsNullOrEmpty(copySource.partitionOption) &&
            copySource.partitionOption !== partitionOptionConst.none
        ) {
            if (copySource.partitionOption === partitionOptionConst.dyamicRange) {
                result.partitionSettings = {
                    partitionColumnName: ValueAdapter.toResource(copySource, OracleCopySource.PARTITION_COLUMN_NAME),
                    partitionUpperBound: ValueAdapter.toResource(copySource, OracleCopySource.PARTITION_UPPER_BOUND),
                    partitionLowerBound: ValueAdapter.toResource(copySource, OracleCopySource.PARTITION_LOWER_BOUND)
                };
            } else if (copySource.partitionOption === partitionOptionConst.physicalPartitionsOfTable) {
                result.partitionSettings = {
                    partitionNames: ValueAdapter.toResource(copySource, OracleCopySource.PARTITION_NAMES)
                };
            }
        }

        result.convertDecimalToInteger = ValueAdapter.toResource(
            copySource,
            OracleCopySource.CONVERT_DECIMAL_TO_INTEGER
        );

        result.numberPrecision = ValueAdapter.toResource(copySource, OracleCopySource.NUMBER_PRECISION);
        result.numberScale = ValueAdapter.toResource(copySource, OracleCopySource.NUMBER_SCALE);

        try {
            result.queryTimeout =
                typeof copySource.queryTimeout === 'number'
                    ? moment.duration(copySource.queryTimeout, 'minutes').format('hh:mm:ss', {
                        trim: false
                    })
                    : ValueAdapter.toResource(copySource, OracleCopySource.QUERY_TIMEOUT);
        } catch {
            this._logError('Failed to convert queryTimeout into TimeSpan');
        }

        return result;
    }

    protected override async _toModel(
        resource: Model.OracleSource,
        copyActivity: CopyActivity
    ): Promise<OracleCopySource> {
        const result = this.createCopySourceWithCommonProperties(
            Metamodel.modelByName(resource.type),
            resource,
            copyActivity
        ) as OracleCopySource;
        if (resource.oracleReaderQuery) {
            ValueAdapter.toModel(result, OracleCopySource.READER_QUERY, resource.oracleReaderQuery);
            result.mode = 'query';
        }

        if (resource.partitionOption != null) {
            ValueAdapter.toModel(result, OracleCopySource.PARTITION_OPTION, resource.partitionOption);
        }
        if (resource.partitionSettings != null) {
            ValueAdapter.toModel(result, OracleCopySource.PARTITION_NAMES, resource.partitionSettings.partitionNames);
            ValueAdapter.toModel(
                result,
                OracleCopySource.PARTITION_COLUMN_NAME,
                resource.partitionSettings.partitionColumnName
            );
            ValueAdapter.toModel(
                result,
                OracleCopySource.PARTITION_LOWER_BOUND,
                resource.partitionSettings.partitionLowerBound
            );
            ValueAdapter.toModel(
                result,
                OracleCopySource.PARTITION_UPPER_BOUND,
                resource.partitionSettings.partitionUpperBound
            );
        }

        ValueAdapter.toModel(result, OracleCopySource.CONVERT_DECIMAL_TO_INTEGER, resource.convertDecimalToInteger);

        if (resource.numberPrecision !== undefined) {
            ValueAdapter.toModel(result, OracleCopySource.NUMBER_PRECISION, resource.numberPrecision);
        } else {
            delete result.numberPrecision;
        }

        if (resource.numberScale !== undefined) {
            ValueAdapter.toModel(result, OracleCopySource.NUMBER_SCALE, resource.numberScale);
        } else {
            delete result.numberScale;
        }

        try {
            if (resource.queryTimeout == null) {
                result.queryTimeout = undefined;
            } else {
                if (typeof resource.queryTimeout === 'string' && !isLogicAppExpression(resource.queryTimeout)) {
                    result.queryTimeout = moment.duration(resource.queryTimeout).asMinutes();
                } else {
                    ValueAdapter.toModel(result, OracleCopySource.QUERY_TIMEOUT, resource.queryTimeout);
                }
            }
        } catch {
            this._logError('Failed to convert queryTimeout into number');
        }
        return result;
    }
}
